<script>
import MessageMixin from '@mixins/messageMixin';
import AkContextMenu from '@components/v2/general/AkContextMenu.vue';

/* SERVICES */
import messageTemplateService from '@services/messageTemplateService';

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif'];
const VIDEO_TYPES = ['video/mp4', 'video/quicktime'];
const MAX_IMAGE_SIZE = 5 * 1000 * 1000;
const MAX_VIDEO_SIZE = 100 * 1000 * 1000;
const MAX_VIDEO_DURATION = 60;

export default {
  name: 'MessageMediaMenu',
  components: {AkContextMenu},
  mixins: [MessageMixin],
  props: {
    media: {
      type: Array,
      required: true,
    },
    threadId: {
      type: [Number, String],
      required: true,
    },
  },
  emits: ['update:media', 'select-template'],
  computed: {
    mediaMenuItems() {
      return [
        {key: 'image', icon: 'ga-image', label: 'Image'},
        {key: 'video', icon: 'ga-video', label: 'Vidéo'},
        {
          key: 'template',
          icon: 'ga-document',
          label: this.$t('threads.menu.template'),
          submenu: {
            placeholder: this.$t('threads.menu.template_search_placeholder'),
            loadItems: this.loadMessageTemplates,
          },
        },
      ];
    },
  },
  methods: {
    onMenuSelect(key, child) {
      if (key === 'template') {
        this.$emit('select-template', child.preview);
        return;
      }
      this.openFilePicker(key);
    },
    loadMessageTemplates() {
      return messageTemplateService
        .forThread(this.threadId)
        .then((templates) => templates.map((t) => ({value: t.id, label: t.label, preview: t.content})));
    },
    openFilePicker(type) {
      this.$refs[type === 'image' ? 'imageInput' : 'videoInput'].click();
    },
    async onFilesSelected(event) {
      const files = Array.from(event.target.files);
      event.target.value = '';
      const media = [...this.media];
      for (const file of files) {
        const isVideo = file.type.startsWith('video/');
        const error = isVideo ? await this.getVideoError(file) : this.getImageError(file);
        if (error) {
          this.addError(error);
          continue;
        }
        const url = URL.createObjectURL(file);
        media.push({file, url, type: isVideo ? 'video' : 'image'});
      }
      this.$emit('update:media', media);
    },
    getImageError(file) {
      if (!IMAGE_TYPES.includes(file.type)) return 'Format d’image non supporté (PNG, JPEG ou GIF uniquement).';
      if (file.size > MAX_IMAGE_SIZE) return 'L’image dépasse la taille maximale de 5 Mo.';
      return null;
    },
    getVideoError(file) {
      if (!VIDEO_TYPES.includes(file.type)) {
        return Promise.resolve('Format de vidéo non supporté (MP4 ou MOV uniquement).');
      }
      if (file.size > MAX_VIDEO_SIZE) return Promise.resolve('La vidéo dépasse la taille maximale de 100 Mo.');
      return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
          URL.revokeObjectURL(url);
          resolve(video.duration > MAX_VIDEO_DURATION ? 'La vidéo dépasse la durée maximale de 60 secondes.' : null);
        };
        video.onerror = () => {
          URL.revokeObjectURL(url);
          resolve('Impossible de lire cette vidéo.');
        };
        video.src = url;
      });
    },
  },
};
</script>

<template>
  <div class="relative">
    <AkContextMenu
      :items="mediaMenuItems"
      trigger-class="h-12 w-12 flex justify-center items-center bg-primary text-white rounded-full hover:bg-primary-700 focus-visible:bg-primary-700 transition-colors duration-75"
      @select="onMenuSelect">
      <template #trigger>
        <span class="ga-icon ga-plus text-xl font-bold" />
      </template>
    </AkContextMenu>

    <input
      ref="imageInput"
      type="file"
      accept="image/png,image/jpeg,image/gif"
      multiple
      class="hidden"
      @change="onFilesSelected" />
    <input
      ref="videoInput"
      type="file"
      accept="video/mp4,video/quicktime"
      multiple
      class="hidden"
      @change="onFilesSelected" />
  </div>
</template>
